import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Users, Database, CheckCircle, AlertTriangle, Crown } from "lucide-react";
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getStripePriceIds } from '@shared/stripe-config';

interface UpgradeCTAModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'user_limit' | 'license_limit' | 'application_limit';
  currentPlan: string;
  currentUsage: number;
  limit: number;
  nextPlan?: string;
}

// Plan details with environment-aware price IDs
function getPlanDetails() {
  const priceIds = getStripePriceIds();
  return {
    'Single': {
      name: 'Single',
      price: 29,
      users: 10,
      licenses: 120,
      applications: 50,
      priceId: priceIds.single
    },
    'Team': {
      name: 'Team',
      price: 49,
      users: 25,
      licenses: 250,
      applications: 100,
      priceId: priceIds.team
    },
    'Enterprise': {
      name: 'Enterprise',
      price: 199,
      users: -1,
      licenses: -1,
      applications: -1,
      priceId: priceIds.enterprise
    }
  };
}

export function UpgradeCTAModal({
  isOpen,
  onClose,
  limitType,
  currentPlan,
  currentUsage,
  limit,
  nextPlan
}: UpgradeCTAModalProps) {
  const [upgrading, setUpgrading] = useState(false);
  const { toast } = useToast();

  // Fetch real subscription data to get actual usage
  const { data: subscriptionData } = useQuery<any>({
    queryKey: ['/api/subscription-status'],
    enabled: isOpen, // Only fetch when modal is open
  });

  const upgradeMutation = useMutation({
    mutationFn: async (targetPlan: string) => {
      const planDetails = getPlanDetails()[targetPlan as keyof ReturnType<typeof getPlanDetails>];
      if (!planDetails) throw new Error('Invalid plan');

      const result = await apiRequest('/api/subscription/upgrade-checkout', {
        method: 'POST',
        body: {
          planId: targetPlan === 'Team' ? 3 : 4,
          priceId: planDetails.priceId
        }
      });

      return result;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else if (data.sessionId) {
        // Handle session ID if no direct URL
        toast({
          title: "Upgrade initiated",
          description: "Redirecting to checkout...",
        });
      } else {
        toast({
          title: "Upgrade initiated",
          description: "Redirecting to checkout...",
        });
      }
    },
    onError: (error: any) => {
      console.error('Upgrade error:', error);
      toast({
        title: "Upgrade failed",
        description: error.message || "Failed to start upgrade process",
        variant: "destructive",
      });
      setUpgrading(false);
    }
  });

  const handleUpgrade = async (targetPlan: string) => {
    setUpgrading(true);
    upgradeMutation.mutate(targetPlan);
  };

  const getRecommendedPlan = () => {
    if (nextPlan) return nextPlan;
    if (currentPlan === 'Single') return 'Team';
    if (currentPlan === 'Team') return 'Enterprise';
    return 'Enterprise';
  };

  const getLimitIcon = () => {
    switch (limitType) {
      case 'user_limit': return <Users className="w-5 h-5" />;
      case 'license_limit': return <Database className="w-5 h-5" />;
      default: return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const getLimitText = () => {
    switch (limitType) {
      case 'user_limit': return 'Users';
      case 'license_limit': return 'Licenses';
      case 'application_limit': return 'Applications';
      default: return 'Items';
    }
  };

  const recommendedPlan = getRecommendedPlan();
  const recommendedPlanDetails = getPlanDetails()[recommendedPlan as keyof ReturnType<typeof getPlanDetails>];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              {getLimitIcon()}
            </div>
            <span>Upgrade Required</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Limit Alert */}
          <Alert className="border-orange-200 bg-orange-50">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <div className="font-medium">
                You've reached your {getLimitText().toLowerCase()} limit
              </div>
              <div className="text-sm mt-1">
                Current usage: <strong>{subscriptionData?.usage?.licenses || currentUsage}</strong> / {subscriptionData?.limits?.licenses || limit} {getLimitText().toLowerCase()} on your <strong>{subscriptionData?.plan?.name || currentPlan}</strong> plan
              </div>
            </AlertDescription>
          </Alert>

          {/* Recommended Upgrade */}
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Crown className="w-5 h-5 text-blue-600" />
                  <span>Recommended: {recommendedPlanDetails.name} Plan</span>
                </CardTitle>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  Recommended
                </Badge>
              </div>
              <CardDescription>
                Perfect for your current needs with room to grow
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {recommendedPlanDetails.users === -1 ? '∞' : recommendedPlanDetails.users}
                  </div>
                  <div className="text-sm text-gray-600">Users</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {recommendedPlanDetails.licenses === -1 ? '∞' : recommendedPlanDetails.licenses}
                  </div>
                  <div className="text-sm text-gray-600">Licenses</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {recommendedPlanDetails.applications === -1 ? '∞' : recommendedPlanDetails.applications}
                  </div>
                  <div className="text-sm text-gray-600">Apps</div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <div className="text-2xl font-bold">${recommendedPlanDetails.price}</div>
                  <div className="text-sm text-gray-600">per month</div>
                </div>
                <Button 
                  onClick={() => handleUpgrade(recommendedPlan)}
                  disabled={upgrading}
                  className="flex items-center space-x-2"
                >
                  {upgrading ? (
                    <span>Upgrading...</span>
                  ) : (
                    <>
                      <span>Upgrade Now</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Benefits */}
          <div className="space-y-3">
            <h4 className="font-medium">What you'll get with {recommendedPlanDetails.name}:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {[
                'Instant access to add more ' + getLimitText().toLowerCase(),
                'Advanced analytics and reporting',
                'Priority customer support',
                'Team collaboration features',
                'Enhanced security features',
                'Cancel anytime'
              ].map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Not Now
            </Button>
            <div className="space-x-2">
              {recommendedPlan !== 'Enterprise' && (
                <Button 
                  variant="outline" 
                  onClick={() => handleUpgrade('Enterprise')}
                  disabled={upgrading}
                >
                  Enterprise ($199)
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}